import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Element, NodeContext, useEditor } from "@nkitaa/builder-engine";
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandTwitter,
  IconMenu2,
  IconX,
} from "@tabler/icons-react";
import {
  resolveBodyColor,
  resolveBodyFontFamily,
  resolveHyperlinkFontColor,
  resolveHyperlinkHoverColor,
  resolveHyperlinkUnderline,
} from "../theme/themeValueResolver";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { resolveText, type LangText } from "../lib/resolve-text";
import { resolveWbChrome } from "../locale/wbChrome";
import { submitLogout } from "../../lib/runtimePage";
import { navigateOrScroll } from "../lib/linkTarget";
import { clearSession } from "../../lib/auth/sessionStore";
import {
  type AppBarSectionKey,
  type BorderSide,
  type ParentFilter,
  type RuntimeProps,
  AppBarTransparentContext,
  WB_FIELD_CHANGED_EVENT,
  classNames,
  evaluateVisibilityFilter,
  extractUtilityClasses,
  getBorderClasses,
  isVisible,
  normalizeAppBarSectionOrder,
  sanitizeImageSrc,
  toCssLength,
  useAppBarTransparent,
} from "./shared";
import { Button } from "./button";
import { ImageChild } from "./imageChild";
// PR-A4 / H9 — `link.url` and `social.url` come straight from the
// cockpit's Craft.js config. Wrap every `<a href={...}>` so a
// `javascript:` / `data:` smuggled into the JSON renders as inert.
import { safeHref } from "../lib/safe-href";

const STATUS_ALIASES = ["state", "acceptanceState", "status"];

/**
 * Builds a user record for per-link visibility evaluation, merging the session
 * user data with live field values — identical to what nodeVisibilityFn uses
 * for component-level visibility in App.tsx.
 */
export function useLinkVisibilityUser(): Record<string, unknown> {
  const { session } = useWBTheme();
  const [liveFieldValues, setLiveFieldValues] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    function onFieldChanged(e: Event) {
      const { field, value } = (e as CustomEvent).detail as {
        field: string;
        value: string;
      };
      setLiveFieldValues((prev) => {
        const next = { ...prev, [field]: value };
        if (STATUS_ALIASES.includes(field)) {
          for (const alias of STATUS_ALIASES) {
            next[alias] = value;
          }
        }
        return next;
      });
    }

    window.addEventListener(WB_FIELD_CHANGED_EVENT, onFieldChanged);
    return () => {
      window.removeEventListener(WB_FIELD_CHANGED_EVENT, onFieldChanged);
    };
  }, []);

  return { ...(session?.user ?? {}), ...liveFieldValues };
}

export function filterVisibleLinks<
  T extends { isVisible?: boolean; visibilityFilter?: ParentFilter },
>(links: T[], user: Record<string, unknown>): T[] {
  return links.filter((link) => {
    if (link.isVisible === false) return false;
    return evaluateVisibilityFilter(link.visibilityFilter, user);
  });
}

export function AppBarLogo({
  isVisible = true,
  src,
  width,
  height,
}: {
  isVisible?: boolean;
  src?: string;
  width?: string;
  height?: string;
}) {
  const widthClasses = extractUtilityClasses(width, "w-");
  const heightClasses = extractUtilityClasses(height, "h-");
  if (!isVisible) return null;
  return (
    <img
      className={`${widthClasses} ${heightClasses}`.trim() || undefined}
      src={sanitizeImageSrc(src)}
      alt="logo"
      style={{
        width: widthClasses ? undefined : toCssLength(width),
        height: heightClasses ? undefined : toCssLength(height),
        objectFit: "contain",
      }}
    />
  );
}

export function linkClick(
  navigate: ReturnType<typeof useNavigate>,
  event: React.MouseEvent<HTMLAnchorElement>,
  href: string,
  linkType: "internal" | "external" = "internal",
) {
  if (linkType === "external") return;
  event.preventDefault();
  // "/slug" → navigate; "/slug#id"/"#id" → navigate then scroll, or scroll in
  // place when the section is on the current page.
  navigateOrScroll(navigate, href);
}

export function AppBarLinks({
  isVisible = true,
  links,
  fontSize,
  fontWeight,
  fontType,
  color,
  underline: underlineProp = false,
  selectedColor,
  transparentColor,
  transparentSelectedColor,
}: {
  isVisible?: boolean;
  links?: Array<{
    label: LangText;
    url: string;
    linkType?: "internal" | "external";
    isVisible?: boolean;
    visibilityFilter?: ParentFilter;
  }>;
  fontSize?: string;
  fontWeight?: string;
  fontType?: string;
  color?: string;
  underline?: boolean;
  selectedColor?: string;
  transparentColor?: string;
  transparentSelectedColor?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentColorPalette, currentFonts, hyperlinkTheme } = useWBTheme();
  const { currentLang, primaryLanguage } = useWBLang();
  const isTransparentActive = useAppBarTransparent();
  const visibilityUser = useLinkVisibilityUser();
  if (!isVisible) return null;

  const defaultLinkColor = resolveHyperlinkFontColor({
    override: color,
    hyperlinkTheme,
    palette: currentColorPalette,
    allowInherit: false,
  });
  const hoverLinkColor = resolveHyperlinkHoverColor({
    hyperlinkTheme,
    palette: currentColorPalette,
  });
  const themeUnderline = resolveHyperlinkUnderline(hyperlinkTheme);
  const resolvedSelectedColor = selectedColor ?? hoverLinkColor;

  const effectiveLinkColor = isTransparentActive && transparentColor
    ? transparentColor
    : defaultLinkColor;
  const effectiveSelectedColor = isTransparentActive && transparentSelectedColor
    ? transparentSelectedColor
    : resolvedSelectedColor;
  const effectiveHoverColor = isTransparentActive && transparentSelectedColor
    ? transparentSelectedColor
    : hoverLinkColor;

  const isLinkActive = (
    url: string,
    linkType: "internal" | "external" = "internal",
  ) => {
    if (linkType === "external") return false;
    let slug = url;
    if (!slug.startsWith("/")) slug = "/" + slug;
    if (slug.length > 1 && slug.endsWith("/")) slug = slug.slice(0, -1);
    const currentPath = location.pathname;
    return currentPath === slug || (slug === "/" && currentPath === "/home");
  };

  const resolvedLinks = filterVisibleLinks(
    links ?? [
      { label: "Home", url: "/", linkType: "internal" as const },
      { label: "About", url: "/about", linkType: "internal" as const },
      { label: "Services", url: "/services", linkType: "internal" as const },
      { label: "Contact", url: "/contact", linkType: "internal" as const },
    ],
    visibilityUser,
  );
  const linkSizeClasses = extractUtilityClasses(fontSize, "text-");
  const linkWeightClasses = extractUtilityClasses(fontWeight, "font-");

  return (
    <nav style={{ display: "flex", gap: "1.5rem" }}>
      {resolvedLinks.map((link, idx) => {
        const active = isLinkActive(link.url, link.linkType || "internal");
        const linkColor = active ? effectiveSelectedColor : effectiveLinkColor;
        const showUnderline = underlineProp || themeUnderline || active;
        return (
          <a
            key={`${link.url}-${idx}`}
            className={classNames(
              linkSizeClasses || undefined,
              linkWeightClasses || undefined,
              "transition-colors duration-200",
              showUnderline ? "underline" : "no-underline",
              "hover:underline",
            )}
            href={safeHref(link.url)}
            target={link.linkType === "external" ? "_blank" : undefined}
            rel={
              link.linkType === "external" ? "noopener noreferrer" : undefined
            }
            onClick={(event) =>
              linkClick(navigate, event, link.url, link.linkType)
            }
            style={{
              color: linkColor,
              fontSize: toCssLength(fontSize),
              fontFamily: resolveBodyFontFamily({
                override: fontType,
                fonts: currentFonts,
              }),
              transition: "color 0.3s ease",
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.color = effectiveHoverColor;
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.color = linkColor;
            }}
          >
            {resolveText(link.label, currentLang, primaryLanguage)}
          </a>
        );
      })}
    </nav>
  );
}

export type SupportedSocialPlatform =
  | "facebook"
  | "twitter"
  | "instagram"
  | "linkedin";

export type NormalizedSocialIcon = {
  platform: SupportedSocialPlatform;
  url: string;
};

const DEFAULT_SOCIAL_ICONS: NormalizedSocialIcon[] = [
  { platform: "facebook", url: "https://facebook.com" },
  { platform: "twitter", url: "https://twitter.com" },
  { platform: "instagram", url: "https://instagram.com" },
  { platform: "linkedin", url: "https://linkedin.com" },
];

function normalizeSocialPlatform(
  platform: unknown,
  url?: unknown,
): SupportedSocialPlatform | undefined {
  const normalized =
    typeof platform === "string" ? platform.trim().toLowerCase() : undefined;

  switch (normalized) {
    case "facebook":
    case "fb":
      return "facebook";
    case "twitter":
    case "x":
    case "x-twitter":
      return "twitter";
    case "instagram":
    case "ig":
      return "instagram";
    case "linkedin":
    case "linked-in":
      return "linkedin";
    default:
      break;
  }

  if (typeof url !== "string") return undefined;
  const normalizedUrl = url.toLowerCase();
  if (normalizedUrl.includes("facebook.com") || normalizedUrl.includes("fb.me"))
    return "facebook";
  if (normalizedUrl.includes("twitter.com") || normalizedUrl.includes("x.com"))
    return "twitter";
  if (normalizedUrl.includes("instagram.com")) return "instagram";
  if (normalizedUrl.includes("linkedin.com")) return "linkedin";

  return undefined;
}

export function normalizeSocialIcons(
  socialIcons: unknown,
  fallback: NormalizedSocialIcon[] = DEFAULT_SOCIAL_ICONS,
): NormalizedSocialIcon[] {
  if (!Array.isArray(socialIcons)) return fallback;

  const normalized = socialIcons
    .map((social, index) => {
      if (!social || typeof social !== "object" || Array.isArray(social))
        return null;

      const record = social as Record<string, unknown>;
      const fallbackEntry = fallback[index] ?? fallback[0];
      if (!fallbackEntry) return null;

      // An ABSENT url means "never configured" — legacy docs seeded rows
      // without one, and the positional fallback is what fills them in.
      // An EMPTY STRING means the organiser deliberately cleared the field,
      // and substituting a default there published a link they had just
      // deleted: the icon stayed, pointing at whatever sat at that INDEX in
      // DEFAULT_SOCIAL_ICONS (row 0 → facebook, regardless of its platform).
      // The editor showed the row as empty, so the two disagreed.
      if (typeof record.url === "string" && record.url.trim().length === 0) {
        return null;
      }

      const rawUrl = typeof record.url === "string" ? record.url.trim() : "";
      const url = rawUrl.length > 0 ? rawUrl : fallbackEntry.url;
      const platform =
        normalizeSocialPlatform(record.platform, url) ?? fallbackEntry.platform;

      return { platform, url };
    })
    .filter((social): social is NormalizedSocialIcon => social !== null);

  // Once the document HAS a socialIcons array it owns the answer, empty
  // included. Falling back here meant clearing the last row (or all of them)
  // republished the four stock icons — the same "deleted link comes back"
  // surprise as the per-row substitution above. A missing/garbage prop still
  // falls back, at the top of this function.
  return normalized;
}

export function iconForPlatform(
  platform: SupportedSocialPlatform,
  color: string,
  size: number,
) {
  switch (platform) {
    case "facebook":
      return <IconBrandFacebook color={color} size={size} />;
    case "twitter":
      return <IconBrandTwitter color={color} size={size} />;
    case "instagram":
      return <IconBrandInstagram color={color} size={size} />;
    case "linkedin":
      return <IconBrandLinkedin color={color} size={size} />;
    default:
      return null;
  }
}

export function AppBarSocial({
  isVisible = false,
  socialIcons,
  color,
  size = 24,
  position = "left",
  transparentColor,
  fullWidth = false,
  gap = "1rem",
}: {
  isVisible?: boolean | undefined;
  socialIcons?: Array<{ platform: string; url: string }> | undefined;
  color?: string | undefined;
  size?: number | undefined;
  position?: "left" | "center" | "right" | undefined;
  transparentColor?: string | undefined;
  // The AppBar top bar uses content-width social icons (editor AppBarSocial has
  // no w-full); only Contact spans full width so its position justify works
  // (editor ContactSocial has w-full + gap-3). fullWidth/gap let the one shared
  // component serve both without the top bar blowing out its justify-between row.
  fullWidth?: boolean | undefined;
  gap?: string | undefined;
}) {
  const { currentColorPalette, bodyTheme } = useWBTheme();
  const isTransparentActive = useAppBarTransparent();
  if (!isVisible) return null;

  const iconColor = resolveBodyColor({
    override: color,
    bodyTheme,
    palette: currentColorPalette,
  });
  const effectiveIconColor = isTransparentActive && transparentColor
    ? transparentColor
    : iconColor;
  const resolvedSocialIcons = normalizeSocialIcons(socialIcons);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        width: fullWidth ? "100%" : undefined,
        gap,
        justifyContent:
          position === "center"
            ? "center"
            : position === "right"
              ? "flex-end"
              : "flex-start",
      }}
    >
      {resolvedSocialIcons.map((social, idx) => (
        <a
          key={`${social.url}-${idx}`}
          href={safeHref(social.url)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: effectiveIconColor,
            transition: "color 0.3s ease",
            textDecoration: "none",
            lineHeight: 1,
            display: "inline-flex",
          }}
        >
          {iconForPlatform(social.platform, "currentColor", size)}
        </a>
      ))}
    </div>
  );
}

export function ContactSocial({
  padding,
  backgroundColor,
  border = false,
  borderColor,
  borderWidth,
  borderStyle,
  borderSides,
  borderRadius,
  ...socialProps
}: {
  isVisible?: boolean;
  socialIcons?: Array<{ platform: string; url: string }> | undefined;
  color?: string;
  size?: number;
  position?: "left" | "center" | "right";
  padding?: string;
  backgroundColor?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
  borderRadius?: string;
}) {
  const { currentRounding, currentColorPalette } = useWBTheme();
  const borderCls = getBorderClasses(border, borderWidth || "border", borderStyle || "border-solid", borderSides);
  const roundedCls = extractUtilityClasses(
    borderRadius ?? (border ? currentRounding : undefined),
    "rounded",
  );
  return (
    <div
      className={classNames("w-full", padding, borderCls, roundedCls)}
      style={{
        backgroundColor: backgroundColor || undefined,
        borderColor: border ? (borderColor || currentColorPalette.secondary) : undefined,
        borderRadius: roundedCls ? undefined : toCssLength(borderRadius ?? (border ? currentRounding : undefined)),
      }}
    >
      <AppBarSocial {...socialProps} fullWidth gap="0.75rem" />
    </div>
  );
}

type AppBarLink = {
  label: string;
  url: string;
  linkType?: "internal" | "external";
  isVisible?: boolean;
  visibilityFilter?: ParentFilter;
};

/**
 * Read the linked AppBarLinks child node's data from the editor store.
 * This mirrors the builder's `useEditor` selector so that `hideLinks` and the
 * mobile menu use the *child* node's links (the source of truth) rather than
 * the parent's stale `props.links` which may be undefined or outdated.
 */
function useLinkedLinksData() {
  const nodeId = useContext(NodeContext);
  const fallback = {
    linkedLinks: undefined as AppBarLink[] | undefined,
    linkedLinksCount: -1,
    linkedTransparentHamburgerColor: undefined as string | undefined,
  };
  return useEditor((state) => {
    if (!nodeId) return fallback;
    const node = state.nodes[nodeId];
    if (!node) return fallback;
    const linkedId = node.data.linkedNodes["links"];
    if (!linkedId) return fallback;
    const linkedNode = state.nodes[linkedId];
    if (!linkedNode) return fallback;
    const childLinks = linkedNode.data.props.links as AppBarLink[] | undefined;
    return {
      linkedLinks: childLinks,
      linkedLinksCount: Array.isArray(childLinks) ? childLinks.length : -1,
      linkedTransparentHamburgerColor: linkedNode.data.props.transparentMobileHamburgerColor as string | undefined,
    };
  });
}

export function AppBarSection(props: RuntimeProps) {
  const { currentColorPalette, currentFonts, currentRounding, bodyTheme, session } =
    useWBTheme();
  // M6: Logout only makes sense with an authenticated guest session —
  // anonymous visitors must not see the button (session comes from the
  // same theme context useLinkVisibilityUser reads).
  const isAuthenticated = session?.authenticated === true;
  const { currentLang, primaryLanguage } = useWBLang();
  const wbChrome = resolveWbChrome(currentLang, primaryLanguage);
  const visibilityUser = useLinkVisibilityUser();
  const navigate = useNavigate();
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const transparent = (props.transparent as boolean | undefined) ?? false;
  const navbarRef = useRef<HTMLDivElement>(null);
  const [appBarHeight, setAppBarHeight] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const stripped = (props.stripped as boolean | undefined) ?? false;
  const sectionOrder = normalizeAppBarSectionOrder(props.sectionOrder);

  // Read links from the linked child node (source of truth) with fallback to
  // the parent's backward-compat flat prop for older serialized trees.
  const { linkedLinks, linkedLinksCount, linkedTransparentHamburgerColor } = useLinkedLinksData();
  const links = linkedLinks ?? (props.links as AppBarLink[] | undefined);
  const hideLinks =
    linkedLinksCount >= 0
      ? linkedLinksCount <= 1
      : Array.isArray(links) && links.length <= 1;

  useEffect(() => {
    if (!navbarRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setAppBarHeight(Math.round(entry.target.getBoundingClientRect().height));
      }
    });
    resizeObserver.observe(navbarRef.current);
    setAppBarHeight(Math.round(navbarRef.current.getBoundingClientRect().height));
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!transparent || !navbarRef.current) {
      setIsScrolled(false);
      return;
    }
    const findScrollParent = (el: HTMLElement): HTMLElement | Window => {
      let parent = el.parentElement;
      while (parent) {
        const { overflowY } = getComputedStyle(parent);
        if (overflowY === "auto" || overflowY === "scroll") return parent;
        parent = parent.parentElement;
      }
      return window;
    };
    const scrollParent = findScrollParent(navbarRef.current);
    const handleScroll = () => {
      const scrollTop =
        scrollParent === window
          ? window.scrollY
          : (scrollParent as HTMLElement).scrollTop;
      setIsScrolled(scrollTop > 10);
    };
    handleScroll();
    const target = scrollParent === window ? window : scrollParent;
    target.addEventListener("scroll", handleScroll, { passive: true });
    return () => target.removeEventListener("scroll", handleScroll);
  }, [transparent]);

  const isTransparentActive = !!transparent && !isScrolled;

  const mobileHamburgerColor = props.mobileHamburgerColor as string | undefined;
  const mobileMenuBackgroundColor = props.mobileMenuBackgroundColor as
    | string
    | undefined;
  const mobileMenuTextColor = props.mobileMenuTextColor as string | undefined;

  const roundedValue = String(props.borderRadius ?? currentRounding);
  const roundedClasses = extractUtilityClasses(roundedValue, "rounded");
  const roundedBottomClasses = roundedClasses
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => token.replace(/^rounded-/, "rounded-b-"))
    .join(" ");
  const headerBorderClasses = getBorderClasses(
    (props.border as boolean | undefined) ?? true,
    (props.borderWidth as string | undefined) ?? "border-2",
    (props.borderStyle as string | undefined) ?? "border-solid",
    (props.borderSides as BorderSide[] | undefined) ?? ["bottom"],
  );

  if (!isVisible(props.isVisible as boolean | undefined)) return null;

  async function onLogoutClick() {
    if (logoutLoading) return;
    setLogoutLoading(true);

    try {
      const response = await submitLogout();
      if (response.success === false) {
        throw new Error(String(response.message ?? "Logout failed."));
      }
    } catch {
      // Keep flow resilient and route to login so the user can re-authenticate.
    } finally {
      clearSession("logout");
      navigate("/login", { replace: true });
      setLogoutLoading(false);
    }
  }

  function renderSection(sectionId: AppBarSectionKey) {
    switch (sectionId) {
      case "logo":
        return (
          <div key="logo" className="flex items-center">
            <Element
              is={ImageChild}
              id="logo"
              displayName="AppBarLogoChild"
              src={String(props.logoSrc ?? "")}
              width={(props.logoWidth as string | undefined) ?? "w-32"}
              height={(props.logoHeight as string | undefined) ?? "h-16"}
              objectFit="object-contain"
              objectPosition="object-left"
              rounded="rounded-none"
              isVisible={Boolean(props.isLogoVisible ?? true)}
            />
          </div>
        );
      case "links":
        return (
          <div
            key="links"
            className={classNames(
              "items-center",
              stripped || hideLinks ? "hidden" : "hidden wb-md:flex",
            )}
          >
            <Element
              is={AppBarLinks}
              id="links"
              isVisible={Boolean(props.isLinksVisible ?? true)}
              links={links}
              fontSize={
                (props.linksFontSize as string | undefined) ?? "text-md"
              }
              fontWeight={
                (props.linksFontWeight as string | undefined) ?? "font-medium"
              }
              fontType={props.linksFontType as string | undefined}
              color={props.linksColor as string | undefined}
              underline={(props.linksUnderline as boolean | undefined) ?? false}
              selectedColor={props.linksSelectedColor as string | undefined}
            />
          </div>
        );
      case "social":
        return (
          <div
            key="social"
            className={classNames(
              "items-center",
              stripped ? "hidden" : "hidden wb-md:flex",
            )}
          >
            <Element
              is={AppBarSocial}
              id="social"
              isVisible={Boolean(props.isSocialVisible ?? true)}
              socialIcons={
                props.socialIcons as
                  | Array<{ platform: string; url: string }>
                  | undefined
              }
              color={props.socialColor as string | undefined}
              size={(props.socialSize as number | undefined) ?? 24}
            />
          </div>
        );
      default:
        return null;
    }
  }

  const resolvedLinks = filterVisibleLinks(
    links ?? [
      { label: "Home", url: "/", linkType: "internal" as const },
      { label: "About", url: "/about", linkType: "internal" as const },
      { label: "Services", url: "/services", linkType: "internal" as const },
      { label: "Contact", url: "/contact", linkType: "internal" as const },
    ],
    visibilityUser,
  );

  const socialIcons = props.socialIcons as
    | Array<{ platform: string; url: string }>
    | undefined;
  const resolvedSocialIcons = normalizeSocialIcons(socialIcons);
  const socialColor = props.socialColor as string | undefined;
  const socialSize = (props.socialSize as number | undefined) ?? 24;

  const hamburgerColor = mobileHamburgerColor ?? currentColorPalette.secondary;
  const menuBgColor =
    mobileMenuBackgroundColor ?? currentColorPalette.background;
  const menuTextColor = mobileMenuTextColor ?? currentColorPalette.secondary;

  return (
    <div
      ref={navbarRef}
      className={classNames(
        "sticky top-0 z-50 w-full websitebuilder",
        (props.padding as string | undefined) ?? "p-3 wb-md:p-4 wb-lg:p-5",
        headerBorderClasses || undefined,
        roundedBottomClasses || undefined,
      )}
      style={{
        backgroundColor: isTransparentActive
          ? "transparent"
          : String(props.backgroundColor ?? currentColorPalette.background),
        borderColor: isTransparentActive
          ? "transparent"
          : String(props.borderColor ?? currentColorPalette.secondary),
        marginBottom: transparent ? -(appBarHeight || 75) : undefined,
        transition: transparent
          ? "background-color 0.3s ease, border-color 0.3s ease"
          : undefined,
        borderRadius: roundedBottomClasses
          ? undefined
          : toCssLength(roundedValue),
        fontFamily: currentFonts.body,
      }}
    >
      <AppBarTransparentContext.Provider value={isTransparentActive}>
      <div className="max-w-275 mx-auto flex items-center justify-between">
        {sectionOrder.map((sectionId) => renderSection(sectionId))}
        {isAuthenticated && (
        <div
          className={classNames(
            "items-center",
            stripped ? "hidden" : "hidden wb-md:flex",
          )}
        >
          <Button
            text={(props.logoutButtonText as LangText) ?? wbChrome.logout}
            size={
              (props.logoutButtonSize as
                | "xlarge"
                | "large"
                | "medium"
                | "small"
                | "xsmall"
                | undefined) ?? "medium"
            }
            border={(props.logoutButtonBorder as boolean | undefined) ?? false}
            backgroundColor={
              props.logoutButtonBackgroundColor as string | undefined
            }
            textColor={props.logoutButtonColor as string | undefined}
            borderColor={props.logoutButtonBorderColor as string | undefined}
            fontType={props.logoutButtonFontType as string | undefined}
            underline={
              (props.logoutButtonUnderline as boolean | undefined) ?? false
            }
            rounded={props.logoutButtonRounded as string | undefined}
            disabled={logoutLoading}
            loading={logoutLoading}
            onClickAction={onLogoutClick}
            isVisible
          />
        </div>
        )}

        {/* Hamburger button — visible below wb-md, hidden at wb-md+ */}
        {!stripped && (
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            className="flex wb-md:hidden items-center justify-center ml-auto cursor-pointer p-1"
            style={{
              color: isTransparentActive
                ? (linkedTransparentHamburgerColor ?? mobileHamburgerColor ?? currentColorPalette.secondary)
                : hamburgerColor,
              transition: transparent ? "color 0.3s ease" : undefined,
            }}
          >
            {isMobileMenuOpen ? <IconX size={24} /> : <IconMenu2 size={24} />}
          </button>
        )}
      </div>
      </AppBarTransparentContext.Provider>

      {/* Mobile menu dropdown — auto-hidden at wb-md+ */}
      {isMobileMenuOpen && (
        <div
          style={{ backgroundColor: menuBgColor }}
          className="absolute left-0 right-0 top-full shadow-lg z-50 wb-md:hidden"
        >
          <div className="max-w-275 mx-auto p-4">
            {/* Links */}
            {(props.isLinksVisible as boolean | undefined) !== false && (
              <div className="flex flex-col space-y-4 mb-4">
                {resolvedLinks.map((link, idx) => (
                  <a
                    key={`${link.url}-${idx}`}
                    href={safeHref(link.url)}
                    target={link.linkType === "external" ? "_blank" : undefined}
                    rel={
                      link.linkType === "external"
                        ? "noopener noreferrer"
                        : undefined
                    }
                    onClick={(event) => {
                      linkClick(navigate, event, link.url, link.linkType);
                      setIsMobileMenuOpen(false);
                    }}
                    style={{
                      color: menuTextColor,
                      fontFamily: resolveBodyFontFamily({
                        override: props.linksFontType as string | undefined,
                        fonts: currentFonts,
                      }),
                      fontSize: toCssLength(
                        (props.linksFontSize as string | undefined) ??
                          "text-md",
                      ),
                    }}
                    className={classNames(
                      extractUtilityClasses(
                        (props.linksFontSize as string | undefined) ??
                          "text-md",
                        "text-",
                      ) || undefined,
                      extractUtilityClasses(
                        (props.linksFontWeight as string | undefined) ??
                          "font-medium",
                        "font-",
                      ) || undefined,
                      "transition-colors duration-200 no-underline hover:underline",
                    )}
                  >
                    {resolveText(link.label, currentLang, primaryLanguage)}
                  </a>
                ))}
              </div>
            )}

            {/* Social icons */}
            {(props.isSocialVisible as boolean | undefined) !== false && (
              <div className="flex space-x-4 mt-4 mb-4">
                {resolvedSocialIcons.map((social, idx) => (
                  <a
                    key={`${social.url}-${idx}`}
                    href={safeHref(social.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: resolveBodyColor({
                        override: socialColor,
                        bodyTheme,
                        palette: currentColorPalette,
                      }),
                      lineHeight: 1,
                      display: "inline-flex",
                    }}
                  >
                    {iconForPlatform(
                      social.platform,
                      resolveBodyColor({
                        override: socialColor,
                        bodyTheme,
                        palette: currentColorPalette,
                      }),
                      socialSize,
                    )}
                  </a>
                ))}
              </div>
            )}

            {/* Logout button */}
            {isAuthenticated && (
            <div className="mt-4">
              <Button
                text={
                  (props.logoutButtonText as LangText) ?? wbChrome.logout
                }
                size={
                  (props.logoutButtonSize as
                    | "xlarge"
                    | "large"
                    | "medium"
                    | "small"
                    | "xsmall"
                    | undefined) ?? "medium"
                }
                border={
                  (props.logoutButtonBorder as boolean | undefined) ?? false
                }
                backgroundColor={
                  props.logoutButtonBackgroundColor as string | undefined
                }
                textColor={props.logoutButtonColor as string | undefined}
                borderColor={
                  props.logoutButtonBorderColor as string | undefined
                }
                fontType={props.logoutButtonFontType as string | undefined}
                underline={
                  (props.logoutButtonUnderline as boolean | undefined) ?? false
                }
                rounded={props.logoutButtonRounded as string | undefined}
                disabled={logoutLoading}
                loading={logoutLoading}
                onClickAction={onLogoutClick}
                isVisible
              />
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
